import {Globe, ImageOff} from 'lucide-react';
import {
  Badge,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyUrlButton} from '@/components/shared/CopyableUrl';
import {imageMatchTierLabels, type ImageMatchTier} from '@/api/jobs';

type Props = {
  readonly matches: Array<{video_id: string; tier: ImageMatchTier}>;
  readonly offPlatformPageUrls: string[];
};

/**
 The fallback view for a finished Falcon/Pinterest image-search job that produced no items. Matched
 pins are re-collected as real items through the pinterest_pin spider, so they normally render in
 the item grid; this only shows when none came back (every matched pin was deleted by the time it was
 fetched), listing the matched pin ids and off-platform pages as leads to review on Pinterest itself.
 */
// Leads are copied, not followed, so a row is plain text with a copy button rather than a link.
const LEAD_ROW =
  'border-border bg-card flex items-center gap-3 rounded-lg border px-3 py-2 text-xs';
const EYEBROW = 'text-muted-foreground text-[10px] tracking-widest uppercase';

export function ImageSearchLeads({matches, offPlatformPageUrls}: Props) {
  return (
    <div className="mx-auto max-w-2xl space-y-6 py-4">
      {matches.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ImageOff />
            </EmptyMedia>
            <EmptyTitle>No matches</EmptyTitle>
            <EmptyDescription>No matches were found for this image.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="space-y-2">
          <p className={EYEBROW}>Matched Pins</p>
          {matches.map((match) => (
            <div key={match.video_id} className={LEAD_ROW}>
              <span className="text-foreground truncate font-mono select-all">
                {match.video_id}
              </span>
              <Badge variant="outline" className="ml-auto shrink-0">
                {imageMatchTierLabels[match.tier]}
              </Badge>
              <CopyUrlButton
                url={`https://www.pinterest.com/pin/${match.video_id}/`}
                label="Copy pin URL"
                className="shrink-0"
              />
            </div>
          ))}
        </div>
      )}

      {offPlatformPageUrls.length > 0 ? (
        <div className="space-y-2">
          <p className={`${EYEBROW} flex items-center gap-1.5`}>
            <Globe className="size-3.5" />
            Also Found Off-Platform
          </p>
          <p className="text-muted-foreground text-xs">
            Pages outside Pinterest displaying this image &mdash; a repost, storefront, or other
            presence worth checking directly.
          </p>
          {offPlatformPageUrls.map((url) => (
            <div key={url} className={LEAD_ROW}>
              <span className="text-foreground truncate font-mono select-all">{url}</span>
              <CopyUrlButton url={url} className="ml-auto shrink-0" />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
